import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);
const SESSION_HINT = "anytribe_session";

export const PORTAL_PATH = {
  admin: "/internal",
  internal: "/internal",
  creator: "/creator",
  client: "/client",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem(SESSION_HINT, "1");
      setUser(data);
    } catch (e) {
      localStorage.removeItem(SESSION_HINT);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // The httpOnly cookie is the only authority; this hint just avoids a
    // guaranteed-401 request for anonymous visitors on the public site.
    if (localStorage.getItem(SESSION_HINT)) {
      check();
    } else {
      setUser(false);
    }
  }, [check]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(SESSION_HINT, "1");
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem(SESSION_HINT);
      setUser(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh: check, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
